/**
 * Existing Debt Millage Calculator
 *
 * Calculates the millage rate required to service existing debt
 * based on the debt service amount and taxable value for each year.
 */

import type { ProjectData } from '~/hooks/useProjectStore';
import type { Candidate } from '~/types';

/**
 * Calculate millage rates for existing debt service
 *
 * @param candidate - The candidate with tax year plan
 * @param project - The project data with taxable value map
 * @returns Record of year to millage rate for existing debt
 */
export function calculateExistingDebtMillage(
  candidate: Candidate | null,
  project: ProjectData
): Record<number, number> {
  if (!candidate?.taxYearBasedProjectPlan) {
    return {};
  }
  const { taxYearBasedProjectPlan } = candidate;
  const taxableValueMap = project.taxableValueMap || {};

  const millageByYear: Record<number, number> = {};

  // Calculate millage for each year: millage = (existing debt / taxable value) * 1000
  taxYearBasedProjectPlan.forEach((record) => {
    const { taxYear, existingDebt } = record;
    const taxableValue = taxableValueMap[taxYear]?.taxableValue || 0;

    if (taxableValue > 0 && existingDebt > 0) {
      const millage = (1000 * existingDebt) / taxableValue;
      millageByYear[taxYear] = millage;
    }
  });

  return millageByYear;
}

/**
 * Format existing debt millage data for chart display
 *
 * @param millageByYear - Record of year to millage rate
 * @returns Array of data points for chart
 */
export function formatExistingDebtMillageForChart(
  millageByYear: Record<number, number>
): Array<{ year: number; rate: number }> {
  return Object.entries(millageByYear)
    .map(([year, rate]) => ({
      year: parseInt(year, 10),
      rate: Number(rate.toFixed(4)),
    }))
    .sort((a, b) => a.year - b.year);
}
